import type { Components, Theme } from "@mui/material/styles";
import { RadiusToken } from "../../tokens/radii";

/**
 * State cascade (least → most specific):
 *   Enabled  →  Suggested (data attr)  →  Hover  →  Pressed  →
 *   Today  →  Selected  →  Disabled  →  Selected + Disabled
 *
 * "Suggested" is a custom state set by SuggestedPickersDay via the
 * `data-suggested="true"` attribute. It's painted before built-in MUI
 * pseudo-class / class selectors so the user's interaction states still win.
 */
export const MuiPickersDay: NonNullable<Components<Theme>["MuiPickersDay"]> = {
  styleOverrides: {
    root: ({ theme }) => {
      const rmh = theme.vars!.palette.rmh;
      return {
        borderRadius: RadiusToken.MAX,
        ...theme.typography.bodyMdMedium,
        transition: theme.transitions.create(
          ["background-color", "border-color", "color"],
          { duration: theme.transitions.duration.shortest }
        ),

        // Enabled (default for in-month days)
        backgroundColor: rmh.paper["6-brandMuted"].rest,
        color: rmh.text.brand,
        border: "none",

        // Suggested
        "&[data-suggested='true']": {
          backgroundColor: rmh.paper["6-brandMuted"].rest,
          color: rmh.text.brand,
          border: `1.5px solid ${rmh.border["6-brandMuted"].rest}`,
        },

        // Hover
        "&:hover": {
          backgroundColor: rmh.paper["6-brandMuted"].hover,
          color: rmh.text.brand,
        },

        // Pressed
        "&:active": {
          backgroundColor: rmh.paper["6-brandMuted"].active,
          color: rmh.text.brand,
        },

        // Today (no fill — neutral surface with a thin border)
        "&.MuiPickersDay-today": {
          backgroundColor: rmh.paper["1-surface"].rest,
          border: `1.5px solid ${rmh.border["1-surface"].rest}`,
          color: rmh.text.primary,
          "&:hover": {
            backgroundColor: rmh.paper["6-brandMuted"].hover,
          },
        },

        // Selected (filled brand) — overrides Today / Hover / Pressed / Suggested
        "&.Mui-selected": {
          backgroundColor: rmh.paper["5-brandFilled"].rest,
          color: rmh.text.staticWhite,
          border: "none",
          "&:hover, &:focus": {
            backgroundColor: rmh.paper["5-brandFilled"].hover,
            color: rmh.text.staticWhite,
          },
          "&:active": {
            backgroundColor: rmh.paper["5-brandFilled"].active,
            color: rmh.text.staticWhite,
          },
        },

        // Disabled
        "&.Mui-disabled": {
          backgroundColor: rmh.paper["1-surface"].rest,
          color: rmh.text.secondary,
          border: "none",
          "&:hover": {
            backgroundColor: rmh.paper["1-surface"].rest,
          },
        },

        // Selected + Disabled (combined — most specific)
        "&.Mui-selected.Mui-disabled": {
          backgroundColor: rmh.stateBackground.disabled,
          color: rmh.text.secondary,
          border: "none",
        },

        // Days from previous/next month — keep them visible but treat as disabled
        "&.MuiPickersDay-dayOutsideMonth": {
          backgroundColor: rmh.paper["1-surface"].rest,
          color: rmh.text.disabled,
        },
      };
    },
  },
};
